"use client";

import Link from "next/link";
import { ArrowRight, Bot, RefreshCw, Zap, Eye, Sliders, ChevronRight } from "lucide-react";
import { Container, Section } from "@/components/ui/container";
import { SectionHeading } from "@/components/ui/section-heading";
import { Reveal } from "@/components/ui/reveal";
import { Counter } from "@/components/ui/counter";
import {
  HubHero, HubOverview, HubFeatures, HubUseCases, HubWhy, HubFAQ, HubCTA,
  CategoryHubComposer,
} from "@/components/ui/category-hub";
import {
  autonomousStats, autonomousChildren, autonomousFeatures, autonomousUseCases,
  autonomousWhy, autonomousFaq, autonomousCta, loopStages, decisionTree,
} from "@/lib/data/autonomous-hub";

/* ============================================================
   BESPOKE 1 — Closed-loop signature diagram.
   Six stages connected in a circle (Sense -> Simulate -> Decide
   -> Generate -> Deploy -> Measure -> back to Sense).
   ============================================================ */
function ClosedLoopDiagram() {
  const ROUND = (n: number) => Math.round(n * 100) / 100;
  const cx = 260;
  const cy = 260;
  const radius = 175;
  const stages = loopStages;
  return (
    <div className="relative w-full max-w-[520px] aspect-square mx-auto">
      <svg viewBox="0 0 520 520" className="w-full h-full" aria-hidden>
        <defs>
          <radialGradient id="loopCore" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#10b981" stopOpacity="0.85" />
            <stop offset="60%" stopColor="#06b6d4" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#dc143c" stopOpacity="0" />
          </radialGradient>
        </defs>
        <circle cx={cx} cy={cy} r="200" fill="url(#loopCore)" />
        {/* Outer rotating dashed ring */}
        <circle cx={cx} cy={cy} r={radius + 35} fill="none" stroke="rgb(16 185 129 / 0.3)" strokeWidth="0.8" strokeDasharray="3 6" />
        {/* Inner ring */}
        <circle cx={cx} cy={cy} r={radius - 45} fill="none" stroke="rgb(255 255 255 / 0.06)" strokeWidth="0.6" strokeDasharray="1 4" />
        {/* Stage nodes */}
        {stages.map((s, i) => {
          const angle = (i / stages.length) * Math.PI * 2 - Math.PI / 2;
          const x = ROUND(cx + Math.cos(angle) * radius);
          const y = ROUND(cy + Math.sin(angle) * radius);
          const nextAngle = ((i + 1) / stages.length) * Math.PI * 2 - Math.PI / 2;
          const midAngle = (angle + nextAngle) / 2;
          return (
            <g key={s.label}>
              <line x1={cx} y1={cy} x2={x} y2={y} stroke={`var(--color-${s.accent})`} strokeOpacity="0.25" strokeWidth="0.6" strokeDasharray="2 4" />
              <circle cx={x} cy={y} r="34" fill={`color-mix(in oklch, var(--color-${s.accent}) 22%, transparent)`} stroke={`var(--color-${s.accent})`} strokeWidth="0.9" />
              <circle cx={x} cy={y} r="28" fill="rgb(10 10 18 / 0.78)" />
              <text x={x} y={y + 2} textAnchor="middle" fontSize="9" fontWeight="900" fill="white" letterSpacing="0.5">
                {s.label.toUpperCase()}
              </text>
              <text x={x} y={y + 12} textAnchor="middle" fontSize="4.5" fill={`var(--color-${s.accent})`} letterSpacing="0.6">
                0{i + 1}
              </text>
            </g>
          );
        })}
        {/* Direction arrows on circle */}
        {stages.map((s, i) => {
          const nextI = (i + 1) % stages.length;
          const next = stages[nextI]!;
          const startAngle = (i / stages.length) * Math.PI * 2 - Math.PI / 2;
          const endAngle = (nextI / stages.length) * Math.PI * 2 - Math.PI / 2;
          const startX = ROUND(cx + Math.cos(startAngle) * (radius + 35));
          const startY = ROUND(cy + Math.sin(startAngle) * (radius + 35));
          const endX = ROUND(cx + Math.cos(endAngle) * (radius + 35));
          const endY = ROUND(cy + Math.sin(endAngle) * (radius + 35));
          return (
            <line
              key={`arr-${i}`}
              x1={startX} y1={startY} x2={endX} y2={endY}
              stroke={`var(--color-${next.accent})`}
              strokeOpacity="0.4"
              strokeWidth="1.4"
              markerEnd="url(#arrowhead)"
            />
          );
        })}
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="3" refY="3" orient="auto">
            <polygon points="0 0, 6 3, 0 6" fill="rgb(255 255 255 / 0.6)" />
          </marker>
        </defs>
        {/* Center */}
        <g transform={`translate(${cx}, ${cy})`}>
          <circle r="60" fill="rgb(10 10 18 / 0.92)" />
          <circle r="50" fill="none" stroke="rgb(16 185 129 / 0.5)" strokeWidth="1" />
          <text x="0" y="-3" textAnchor="middle" fontSize="22" fontWeight="900" fill="white" letterSpacing="-0.5">
            X2
          </text>
          <text x="0" y="13" textAnchor="middle" fontSize="6" fontWeight="700" fill="#10b981" letterSpacing="2">
            AUTONOMOUS
          </text>
          <text x="0" y="23" textAnchor="middle" fontSize="4.5" fill="rgb(255 255 255 / 0.5)" letterSpacing="1.5">
            6 · 8 · 24/7
          </text>
        </g>
      </svg>
      <div className="absolute top-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "0.5s" }}>
        <RefreshCw className="h-3.5 w-3.5" style={{ color: "var(--color-emerald)" }} />
        <span className="text-xs font-semibold">Closed Loop</span>
      </div>
      <div className="absolute top-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "1.5s" }}>
        <Zap className="h-3.5 w-3.5" style={{ color: "var(--color-crimson)" }} />
        <span className="text-xs font-semibold">&lt;1s Response</span>
      </div>
      <div className="absolute bottom-2 left-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "2.5s" }}>
        <Bot className="h-3.5 w-3.5" style={{ color: "var(--color-cyan)" }} />
        <span className="text-xs font-semibold">8 Surfaces</span>
      </div>
      <div className="absolute bottom-2 right-2 hidden md:flex items-center gap-2 glass rounded-full px-3 py-1.5 animate-float" style={{ animationDelay: "3.5s" }}>
        <Eye className="h-3.5 w-3.5" style={{ color: "var(--color-amber)" }} />
        <span className="text-xs font-semibold">24/7 Monitor</span>
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 2 — Decision-trigger tree visualization.
   Signal -> Critical / Material / Sub-critical -> actions.
   ============================================================ */
function DecisionTreeVisual() {
  type Node = { label: string; children?: Node[] };
  const renderBranch = (node: Node, depth: number, idx: number, accent: string): React.ReactNode => (
    <div key={`${depth}-${idx}-${node.label}`} className="flex flex-col items-stretch">
      <div
        className="glass rounded-xl px-4 py-3 mb-2"
        style={{
          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${accent} 18%, transparent)`,
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="h-1.5 w-1.5 rounded-full"
            style={{ background: accent }}
          />
          <span className="text-sm font-semibold">{node.label}</span>
        </div>
      </div>
      {node.children && node.children.length > 0 && (
        <div className="ml-4 pl-4 border-l border-dashed" style={{ borderColor: `color-mix(in oklch, ${accent} 25%, transparent)` }}>
          {node.children.map((c, i) => renderBranch(c, depth + 1, i, accent))}
        </div>
      )}
    </div>
  );
  const treeAccents = ["var(--color-crimson)", "var(--color-amber)", "var(--color-emerald)"];
  return (
    <div className="space-y-6">
      <div className="glass rounded-2xl p-5" style={{ boxShadow: "inset 0 0 0 1px color-mix(in oklch, var(--color-purple) 18%, transparent)" }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-purple)] animate-pulse" />
          <span className="eyebrow-tiny font-bold uppercase tracking-[0.28em]" style={{ color: "var(--color-purple)" }}>
            Incoming Signal
          </span>
        </div>
        <h3 className="text-lg font-bold">{decisionTree.label}</h3>
        <p className="mt-1 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">
          Every signal is classified by severity and routed to the appropriate response path. Human escalation only on strategic thresholds.
        </p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {decisionTree.children?.map((branch, i) => (
          <div key={branch.label}>
            <div className="mb-3 flex items-center gap-2">
              <span
                className="grid place-items-center h-8 w-8 rounded-lg text-[10px] font-bold tracking-wider"
                style={{
                  background: `color-mix(in oklch, ${treeAccents[i]} 18%, transparent)`,
                  color: treeAccents[i],
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${treeAccents[i]} 35%, transparent)`,
                }}
              >
                {i + 1}
              </span>
              <span className="eyebrow-tiny font-bold uppercase tracking-[0.2em]" style={{ color: treeAccents[i] }}>
                {branch.label} Branch
              </span>
            </div>
            {branch.children && branch.children.map((action, j) => renderBranch(action, 1, j, treeAccents[i]!))}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   BESPOKE 3 — Loop stage detail cards.
   ============================================================ */
function LoopStageCards() {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {loopStages.map((s, i) => (
        <Reveal key={s.label} delay={i * 60}>
          <div
            className="glass rounded-2xl p-5 h-full card-glow relative overflow-hidden"
            style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
          >
            <div className="absolute -top-10 -right-10 h-32 w-32 rounded-full blur-2xl opacity-25" style={{ background: `var(--color-${s.accent})` }} />
            <div className="relative flex items-start gap-3">
              <span
                className="grid place-items-center h-12 w-12 rounded-xl shrink-0 text-sm font-bold tracking-wider"
                style={{
                  background: `color-mix(in oklch, var(--color-${s.accent}) 22%, transparent)`,
                  boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${s.accent}) 40%, transparent)`,
                  color: `var(--color-${s.accent})`,
                }}
              >
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="text-base font-bold">{s.label}</h3>
                <p className="mt-1.5 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{s.description}</p>
              </div>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  );
}

/* ============================================================
   PAGE COMPOSER
   ============================================================ */
export function AutonomousHubPage() {
  return (
    <CategoryHubComposer
      overviewWrap="emerald"
      featuresWrap="crimson"
      useCasesWrap="purple"
      whyWrap="cyan"
      faqWrap="amber"
      ctaWrap="violet"
      hero={
        <section id="top" className="relative overflow-hidden pt-8 pb-16 sm:pt-12 sm:pb-24">
          <div className="pointer-events-none absolute inset-0 -z-10">
            <div className="absolute top-10 left-1/4 h-72 w-72 rounded-full bg-[var(--color-emerald)] opacity-22 blur-[130px] animate-pulse-glow" />
            <div className="absolute top-32 right-1/4 h-80 w-80 rounded-full bg-[var(--color-cyan)] opacity-18 blur-[140px] animate-pulse-glow" style={{ animationDelay: "2s" }} />
            <div className="absolute bottom-10 left-1/3 h-72 w-72 rounded-full bg-[var(--color-crimson)] opacity-15 blur-[140px] animate-pulse-glow" style={{ animationDelay: "4s" }} />
          </div>
          <Container size="wide">
            <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-10 lg:gap-6 items-center">
              <div className="flex flex-col items-start gap-6">
                <Reveal>
                  <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full glass">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full rounded-full bg-[var(--color-emerald)] opacity-75 animate-ping" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-[var(--color-emerald)]" />
                    </span>
                    <span className="eyebrow-tiny uppercase tracking-[0.28em]">Autonomous Operations</span>
                  </div>
                </Reveal>
                <Reveal delay={80}>
                  <h1 className="display-hero text-balance">
                    Zero-Touch{" "}
                    <span className="bg-gradient-to-br from-[var(--color-emerald)] via-[var(--color-cyan)] to-[var(--color-purple)] bg-clip-text text-transparent">
                      Perception Operations
                    </span>
                  </h1>
                </Reveal>
                <Reveal delay={160}>
                  <p className="text-[1.35rem] sm:text-[1.5rem] leading-[1.45] font-medium text-[rgb(var(--fg))] max-w-2xl">
                    Eight autonomous surfaces composed under a closed-loop orchestration. Sub-second response. Conditional escalation. 24/7 operations.
                  </p>
                </Reveal>
                <Reveal delay={220}>
                  <p className="text-body leading-relaxed text-[rgb(var(--fg-muted))] max-w-2xl">
                    From sense to simulate, decide to generate, deploy to measure — every stage autonomous, every decision auditable. The fastest operational cycle in perception management, with humans escalated only on strategic thresholds.
                  </p>
                </Reveal>
                <Reveal delay={280}>
                  <div className="mt-2 flex flex-wrap items-center gap-3">
                    <Link
                      href="/autonomous/adaptive-response"
                      className="btn-primary-gradient h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base shadow-2xl shadow-[var(--color-emerald)/30]"
                    >
                      Explore Adaptive Response <ArrowRight className="h-4 w-4" />
                    </Link>
                    <Link
                      href="/automated-decision-triggers"
                      className="btn-glass h-14 px-8 inline-flex items-center gap-2 rounded-full font-semibold text-base"
                    >
                      Decision Triggers
                    </Link>
                  </div>
                </Reveal>
                <Reveal delay={360}>
                  <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 max-w-3xl w-full">
                    {autonomousStats.map((s) => (
                      <div
                        key={s.label}
                        className="glass rounded-2xl p-3 card-glow"
                        style={{ ["--glow" as string]: `var(--color-${s.accent})` }}
                      >
                        <div className="display-stat-sm" style={{ color: `var(--color-${s.accent})` }}>
                          <Counter to={s.to} suffix={s.suffix ?? ""} decimals={s.decimals ?? 0} />
                        </div>
                        <div className="eyebrow-tiny text-[rgb(var(--fg-subtle))] mt-1.5 font-semibold uppercase tracking-wider">
                          {s.label}
                        </div>
                      </div>
                    ))}
                  </div>
                </Reveal>
              </div>
              <Reveal delay={200} className="order-first lg:order-last">
                <ClosedLoopDiagram />
              </Reveal>
            </div>
          </Container>
        </section>
      }
      overview={
        <HubOverview
          eyebrow="Eight Autonomous Surfaces"
          title="Every Surface of the Autonomous Stack"
          description="Perception X2 organizes autonomous operations into eight purpose-built surfaces — each addressing a distinct operational layer, all composed under a closed-loop orchestration."
          items={autonomousChildren}
          accent="emerald"
        />
      }
      features={
        <>
          <Section id="loop" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Closed-Loop Stages"
                title="Six Stages. Continuous Operation."
                description="Every autonomous engagement follows the same closed loop: Sense, Simulate, Decide, Generate, Deploy, Measure — and back to Sense. The platform never leaves the loop."
                accent="crimson"
              />
              <LoopStageCards />
            </Container>
          </Section>
          <Section id="features" className="relative mt-8">
            <Container>
              <SectionHeading
                eyebrow="Autonomous Capabilities"
                title="What the Autonomous Stack Delivers"
                description="Eight foundational capabilities shared across every autonomous surface — each engineered for machine-speed operation with conditional escalation."
                accent="purple"
              />
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {autonomousFeatures.map((f, i) => (
                  <Reveal key={f.title} delay={i * 50}>
                    <div
                      className="glass rounded-2xl p-5 h-full card-glow"
                      style={{ ["--glow" as string]: `var(--color-${f.accent})` }}
                    >
                      <div
                        className="grid place-items-center h-10 w-10 rounded-lg"
                        style={{
                          background: `color-mix(in oklch, var(--color-${f.accent}) 18%, transparent)`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, var(--color-${f.accent}) 35%, transparent)`,
                        }}
                      >
                        <span className="text-base font-bold" style={{ color: `var(--color-${f.accent})` }}>◆</span>
                      </div>
                      <h3 className="mt-3 text-base font-bold">{f.title}</h3>
                      <p className="mt-2 text-xs text-[rgb(var(--fg-muted))] leading-relaxed">{f.description}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </Container>
          </Section>
        </>
      }
      useCases={
        <HubUseCases
          eyebrow="Autonomous Applications"
          title="Six Operational Patterns"
          description="The Perception X2 autonomous stack serves six primary operational patterns — each tuned to a distinct strategic posture."
          cases={autonomousUseCases}
          accent="purple"
        />
      }
      why={
        <>
          <Section id="decisions" className="relative">
            <Container>
              <SectionHeading
                eyebrow="Decision Tree"
                title="How Signals Become Decisions"
                description="Every incoming perception signal is classified into Critical, Material, or Sub-critical and routed to the appropriate response path. Strategic thresholds trigger human escalation; routine decisions never touch a human queue."
                accent="cyan"
              />
              <DecisionTreeVisual />
            </Container>
          </Section>
          <Section id="why" className="relative mt-8">
            <Container>
              <HubWhy
                eyebrow="Why These Autonomous Surfaces"
                title="The Differentiators That Compound"
                items={autonomousWhy}
                accent="cyan"
              />
            </Container>
          </Section>
        </>
      }
      faq={
        <HubFAQ
          eyebrow="Autonomous FAQ"
          title="Frequently Asked Questions"
          items={autonomousFaq}
          accent="amber"
        />
      }
      cta={
        <HubCTA
          eyebrow={autonomousCta.eyebrow}
          title={autonomousCta.title}
          description={autonomousCta.description}
          primary={autonomousCta.primary}
          secondary={autonomousCta.secondary}
          trustItems={autonomousCta.trustItems}
          accent="violet"
        />
      }
    />
  );
}
